import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './RegistrationPage.css';

const COUNTRIES = [
  'Albania', 'Andorra', 'Armenia', 'Austria', 'Azerbaijan', 'Belarus',
  'Belgium', 'Bosnia & Herzegovina', 'Bulgaria', 'Croatia', 'Cyprus', 'Czechia',
  'Denmark', 'England', 'Estonia', 'Faroe Islands', 'Finland', 'France',
  'Georgia', 'Germany', 'Gibraltar', 'Greece', 'Greenland', 'Hungary',
  'Iceland', 'Ireland', 'Israel', 'Italy', 'Kosovo', 'Latvia',
  'Liechtenstein', 'Lithuania', 'Luxembourg', 'Malta', 'Moldova', 'Monaco',
  'Montenegro', 'The Netherlands', 'Northern Ireland', 'North Macedonia', 'Norway', 'Poland',
  'Portugal', 'Romania', 'Russia', 'San Marino', 'Scotland', 'Serbia',
  'Slovakia', 'Slovenia', 'Spain', 'Sweden', 'Switzerland', 'Türkiye',
  'Ukraine', 'Wales',
];

const AREAS = [
  { value: 'national_team', label: 'National Team Volleyball' },
  { value: 'club', label: 'Club Volleyball' },
  { value: 'beach', label: 'Beach Volleyball' },
  { value: 'snow', label: 'Snow Volleyball' },
  { value: 'development', label: 'Development' },
  { value: 'youth', label: 'Youth Volleyball' },
];

const INITIAL = {
  user_role: '',
  email: '',
  first_name: '',
  last_name: '',
  country: '',
  age_bracket: '',
  phone: '',
  age_group: '',
  gender: '',
  areas_of_interest: [],
  playing_format: '',
  competition_level: '',
  match_date: '',
  match_time: '',
  internal_match_number: '',
  home_team_name: '',
  away_team_name: '',
  home_team_roster: [{ first_name: '', last_name: '', shirt_number: '' }],
  away_team_roster: [{ first_name: '', last_name: '', shirt_number: '' }],
  privacy_accepted: false,
  terms_accepted: false,
};

export default function RegistrationPage() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [serverError, setServerError] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [showMatchDetails, setShowMatchDetails] = useState(false);
  const [publicToken, setPublicToken] = useState('');

  useEffect(() => {
    window.onRecaptchaSuccess = (token) => setRecaptchaToken(token);
    window.onRecaptchaExpired = () => setRecaptchaToken('');
    return () => {
      delete window.onRecaptchaSuccess;
      delete window.onRecaptchaExpired;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox' && name === 'areas_of_interest') {
      setForm(f => ({
        ...f,
        areas_of_interest: checked
          ? [...f.areas_of_interest, value]
          : f.areas_of_interest.filter(a => a !== value),
      }));
    } else if (type === 'checkbox') {
      setForm(f => ({ ...f, [name]: checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
    if (errors[name]) setErrors(e => ({ ...e, [name]: '' }));
  };

  const handleRosterChange = (team, index, field, value) => {
    setForm(f => {
      const roster = [...f[team]];
      roster[index] = { ...roster[index], [field]: value };
      return { ...f, [team]: roster };
    });
  };

  const addPlayer = (team) => {
    setForm(f => ({
      ...f,
      [team]: [...f[team], { first_name: '', last_name: '', shirt_number: '' }],
    }));
  };

  const removePlayer = (team, index) => {
    setForm(f => ({
      ...f,
      [team]: f[team].filter((_, i) => i !== index),
    }));
  };

  const validate = () => {
    const e = {};
    if (!form.user_role) e.user_role = 'Please select your role';
    if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Valid email required';
    if (!form.first_name.trim()) e.first_name = 'First name required';
    if (!form.last_name.trim()) e.last_name = 'Last name required';
    if (!form.country) e.country = 'Country required';
    if (!form.age_bracket) e.age_bracket = 'Age bracket required';
    if (!form.privacy_accepted) e.privacy_accepted = 'You must accept the Privacy Policy';
    if (!form.terms_accepted) e.terms_accepted = 'You must accept the Terms of Use';
    if (!recaptchaToken) e.recaptcha = 'Please complete the reCAPTCHA verification';
    return e;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      const first = document.querySelector('.field-error');
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setStatus('loading');
    setServerError('');
    try {
      const payload = {
        ...form,
        recaptcha_token: recaptchaToken,
        privacy_accepted: String(form.privacy_accepted),
        terms_accepted: String(form.terms_accepted),
        home_team_roster: form.home_team_roster.filter(p => p.first_name.trim() || p.last_name.trim()),
        away_team_roster: form.away_team_roster.filter(p => p.first_name.trim() || p.last_name.trim()),
      };
      const res = await api.post('/registrations', payload);
      setPublicToken(res.data.public_token || '');
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setServerError(err.response?.data?.error || 'Registration failed. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="reg-page">
        <Header />
        <div className="reg-success">
          <div className="success-icon">✓</div>
          <h2>Thank you for registering!</h2>
          <p>You have successfully registered for the CEV School Project e-scoresheet. We'll be in touch soon.</p>
          <button className="btn-primary" onClick={() => { setForm(INITIAL); setStatus('idle'); setPublicToken(''); }}>
            Register another participant
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="reg-page">
      <Header />

      <main className="reg-main">
        <div className="reg-card">
          <div className="reg-card-header">
            <h2>Registration Form</h2>
            <p>Join the CEV School Project e-scoresheet initiative</p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* SECTION 1: Role */}
            <section className="form-section">
              <h3 className="section-title">
                <span className="section-num">01</span> Your Role
              </h3>
              <div className="role-grid">
                {['coach','teacher','referee','volunteer','parent'].map(role => (
                  <label key={role} className={`role-card ${form.user_role === role ? 'active' : ''}`}>
                    <input
                      type="radio"
                      name="user_role"
                      value={role}
                      checked={form.user_role === role}
                      onChange={handleChange}
                    />
                    <span className="role-icon">{roleIcon(role)}</span>
                    <span className="role-label">{role.charAt(0).toUpperCase() + role.slice(1)}</span>
                  </label>
                ))}
              </div>
              {errors.user_role && <p className="field-error">{errors.user_role}</p>}
            </section>

            {/* SECTION 2: Demographics */}
            <section className="form-section">
              <h3 className="section-title">
                <span className="section-num">02</span> Your Details
              </h3>

              <div className="form-row two-col">
                <div className="form-field">
                  <label>First Name <span className="req">*</span></label>
                  <input
                    type="text" name="first_name" value={form.first_name}
                    onChange={handleChange} placeholder="First name"
                    className={errors.first_name ? 'input-error' : ''}
                  />
                  {errors.first_name && <p className="field-error">{errors.first_name}</p>}
                </div>
                <div className="form-field">
                  <label>Last Name <span className="req">*</span></label>
                  <input
                    type="text" name="last_name" value={form.last_name}
                    onChange={handleChange} placeholder="Last name"
                    className={errors.last_name ? 'input-error' : ''}
                  />
                  {errors.last_name && <p className="field-error">{errors.last_name}</p>}
                </div>
              </div>

              <div className="form-field">
                <label>Email Address <span className="req">*</span></label>
                <input
                  type="email" name="email" value={form.email}
                  onChange={handleChange} placeholder="your@email.com"
                  className={errors.email ? 'input-error' : ''}
                />
                {errors.email && <p className="field-error">{errors.email}</p>}
              </div>

              <div className="form-row two-col">
                <div className="form-field">
                  <label>Country <span className="req">*</span></label>
                  <select
                    name="country" value={form.country} onChange={handleChange}
                    className={errors.country ? 'input-error' : ''}
                  >
                    <option value="">Select country…</option>
                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {errors.country && <p className="field-error">{errors.country}</p>}
                </div>
                <div className="form-field">
                  <label>Age Bracket <span className="req">*</span></label>
                  <select
                    name="age_bracket" value={form.age_bracket} onChange={handleChange}
                    className={errors.age_bracket ? 'input-error' : ''}
                  >
                    <option value="">Select age range…</option>
                    <option value="under_18">Under 18</option>
                    <option value="18_24">18 – 24</option>
                    <option value="25_34">25 – 34</option>
                    <option value="35_44">35 – 44</option>
                    <option value="45_54">45 – 54</option>
                    <option value="55_64">55 – 64</option>
                    <option value="65_plus">65+</option>
                  </select>
                  {errors.age_bracket && <p className="field-error">{errors.age_bracket}</p>}
                </div>
              </div>

              <div className="form-field">
                <label>Phone Number <span className="optional">(optional)</span></label>
                <input
                  type="tel" name="phone" value={form.phone}
                  onChange={handleChange} placeholder="+1 234 567 890"
                />
              </div>
            </section>

            {/* SECTION 3: VS Score / Match Info */}
            <section className="form-section">
              <h3 className="section-title">
                <span className="section-num">03</span> e-scoresheet Details
                <span className="section-badge">For QR Code Generation</span>
              </h3>

              <div className="form-row three-col">
                <div className="form-field">
                  <label>Age Group</label>
                  <select name="age_group" value={form.age_group} onChange={handleChange}>
                    <option value="">Select age group…</option>
                    <option value="U10">Under 10 (U10)</option>
                    <option value="U12">Under 12 (U12)</option>
                    <option value="U14">Under 14 (U14)</option>
                    <option value="U16">Under 16 (U16)</option>
                    <option value="U18">Under 18 (U18)</option>
                    <option value="U20">Under 20 (U20)</option>
                    <option value="senior">Senior</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Gender</label>
                  <select name="gender" value={form.gender} onChange={handleChange}>
                    <option value="">Select gender…</option>
                    <option value="girls">Girls</option>
                    <option value="boys">Boys</option>
                    <option value="mixed">Mixed</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Playing Format</label>
                  <select name="playing_format" value={form.playing_format} onChange={handleChange}>
                    <option value="">Select format…</option>
                    <option value="2v2">2 vs 2</option>
                    <option value="3v3">3 vs 3</option>
                    <option value="4v4">4 vs 4</option>
                    <option value="6v6">6 vs 6</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row two-col">
                <div className="form-field">
                  <label>Competition Level</label>
                  <select name="competition_level" value={form.competition_level} onChange={handleChange}>
                    <option value="">Select level…</option>
                    <option value="friendly">Friendly</option>
                    <option value="regional">Regional</option>
                    <option value="national">National</option>
                  </select>
                </div>
              </div>

              <button
                type="button"
                className="toggle-match-btn"
                onClick={() => setShowMatchDetails(v => !v)}
              >
                {showMatchDetails ? '− Hide' : '+ Add'} Match Details (optional)
              </button>

              {showMatchDetails && (
                <div className="match-details-panel">
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label>Match Date</label>
                      <input type="date" name="match_date" value={form.match_date} onChange={handleChange} />
                    </div>
                    <div className="form-field">
                      <label>Match Time</label>
                      <input type="time" name="match_time" value={form.match_time} onChange={handleChange} />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Internal Match Number <span className="optional">(optional)</span></label>
                    <input
                      type="text" name="internal_match_number"
                      value={form.internal_match_number} onChange={handleChange}
                      placeholder="e.g. MATCH-001"
                    />
                  </div>
                  <div className="form-row two-col">
                    <div className="form-field">
                      <label>Home Team Name</label>
                      <input
                        type="text" name="home_team_name"
                        value={form.home_team_name} onChange={handleChange}
                        placeholder="Home team name"
                      />
                    </div>
                    <div className="form-field">
                      <label>Away Team Name</label>
                      <input
                        type="text" name="away_team_name"
                        value={form.away_team_name} onChange={handleChange}
                        placeholder="Away team name"
                      />
                    </div>
                  </div>

                  {/* Roster tables */}
                  {['home_team_roster', 'away_team_roster'].map(team => (
                    <div className="form-field" key={team}>
                      <label>{team === 'home_team_roster' ? 'Home' : 'Away'} Team Roster <span className="optional">(optional)</span></label>
                      <div className="roster-table">
                        <div className="roster-header">
                          <span>#</span>
                          <span>First Name</span>
                          <span>Last Name</span>
                          <span>Shirt No.</span>
                          <span></span>
                        </div>
                        {form[team].map((player, idx) => (
                          <div className="roster-row" key={idx}>
                            <span className="roster-idx">{idx + 1}</span>
                            <input
                              type="text"
                              placeholder="First name"
                              value={player.first_name}
                              onChange={e => handleRosterChange(team, idx, 'first_name', e.target.value)}
                            />
                            <input
                              type="text"
                              placeholder="Last name"
                              value={player.last_name}
                              onChange={e => handleRosterChange(team, idx, 'last_name', e.target.value)}
                            />
                            <input
                              type="number"
                              placeholder="No."
                              min="1" max="99"
                              value={player.shirt_number}
                              onChange={e => handleRosterChange(team, idx, 'shirt_number', e.target.value)}
                              className="shirt-number-input"
                            />
                            <button
                              type="button"
                              className="roster-remove-btn"
                              onClick={() => removePlayer(team, idx)}
                              title="Remove player"
                            >×</button>
                          </div>
                        ))}
                        <button type="button" className="roster-add-btn" onClick={() => addPlayer(team)}>
                          + Add Player
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* SECTION 4: Areas of Interest */}
            <section className="form-section">
              <h3 className="section-title">
                <span className="section-num">04</span> Areas of Interest
                <span className="optional" style={{fontFamily:'var(--font-main)', fontSize:'13px', fontWeight:400, textTransform:'none', letterSpacing:0}}>(optional)</span>
              </h3>
              <div className="interest-grid">
                {AREAS.map(a => (
                  <label key={a.value} className={`interest-card ${form.areas_of_interest.includes(a.value) ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      name="areas_of_interest"
                      value={a.value}
                      checked={form.areas_of_interest.includes(a.value)}
                      onChange={handleChange}
                    />
                    <span>{a.label}</span>
                  </label>
                ))}
              </div>
            </section>

            {/* SECTION 5: Consent */}
            <section className="form-section">
              <h3 className="section-title">
                <span className="section-num">05</span> Consent
              </h3>
              <div className="consent-group">
                <label className={`consent-row ${errors.privacy_accepted ? 'consent-error' : ''}`}>
                  <input
                    type="checkbox" name="privacy_accepted"
                    checked={form.privacy_accepted} onChange={handleChange}
                  />
                  <span>
                    I have read and accept the{' '}
                    <a href="https://inside.cev.eu/privacy-policy/" target="_blank" rel="noopener noreferrer">
                      CEV Privacy Policy
                    </a>
                    <span className="req"> *</span>
                  </span>
                </label>
                {errors.privacy_accepted && <p className="field-error">{errors.privacy_accepted}</p>}

                <label className={`consent-row ${errors.terms_accepted ? 'consent-error' : ''}`}>
                  <input
                    type="checkbox" name="terms_accepted"
                    checked={form.terms_accepted} onChange={handleChange}
                  />
                  <span>
                    I have read and accept the{' '}
                    <a href="https://inside.cev.eu/terms-of-use/" target="_blank" rel="noopener noreferrer">
                      CEV Terms of Use
                    </a>
                    <span className="req"> *</span>
                  </span>
                </label>
                {errors.terms_accepted && <p className="field-error">{errors.terms_accepted}</p>}
              </div>
            </section>

            {serverError && (
              <div className="server-error">{serverError}</div>
            )}

            {/* reCAPTCHA */}
            <div className="recaptcha-wrap">
              <div
                className="g-recaptcha"
                data-sitekey="6LdqcA4tAAAAAFj1WshfCrb4CJJqIDHdH-zQdqAM"
                data-callback="onRecaptchaSuccess"
                data-expired-callback="onRecaptchaExpired"
              />
              {errors.recaptcha && <p className="field-error">{errors.recaptcha}</p>}
            </div>

            <button
              type="submit"
              className="btn-submit"
              disabled={status === 'loading'}
            >
              {status === 'loading' ? (
                <><span className="spinner" /> Submitting…</>
              ) : 'Complete Registration'}
            </button>
          </form>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function Header() {
  return (
    <header className="reg-header">
      <div className="reg-header-inner">
        <div className="reg-header-top">
          <div className="reg-header-logos">
            <img src="/img/cev-logo.png" alt="CEV" className="logo-cev" />
          </div>
          <div className="reg-header-school-logo">
            <img src="/img/school-logo-final.png" alt="School Volleyball" className="logo-school" />
          </div>
        </div>
        <div className="reg-header-title">
          <h1>School Project e-scoresheet</h1>
          <p>Registration Portal</p>
        </div>
      </div>
      <div className="reg-header-wave" />
    </header>
  );
}

function Footer() {
  return (
    <footer className="reg-footer">
      <p>© <a href="https://www.cev.eu" target="_blank" rel="noopener noreferrer">CEV</a> 2020 – {new Date().getFullYear()}</p>
      <p>
        <a href="https://inside.cev.eu/privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        {' · '}
        <a href="https://inside.cev.eu/terms-of-use/" target="_blank" rel="noopener noreferrer">Terms of Use</a>
      </p>
    </footer>
  );
}

function roleIcon(role) {
  const icons = {
    coach: '🏋️',
    teacher: '📚',
    referee: '🟨',
    volunteer: '🤝',
    parent: '👨‍👩‍👦',
  };
  return icons[role] || '👤';
}
